import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";

import { Home02, Settings04, StickerSquare } from "untitledui-js-base";
import moment from "moment";
import { Unit } from "shared/types/unit";
import { Property } from "shared/types/property";
import { Applicant } from "types/application";
import { ApplicationStatusBanner } from "../ApplicationStatusBanner";
import { ApplicationStatus, Invitee, ApplicantType } from "shared/types";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { formatPhone } from "shared/utils";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { formatToCurrency } from "utils/currency";
import { useAuthContext } from "providers/Auth";
import { Checkbox } from "shared/components";
import { Loader } from "shared/components/Loader";
import { toast } from "react-toastify";
import { getFullName } from "shared/utils/get-full-name";
import { usePopoverActions } from "views/applications-id/components/LayoutHeader/hooks/usePopoverActions";

interface ILayoutHeader {
  applicationId: string;
  applicants?: Applicant[];
  invitees: Invitee[];
  unit: Unit;
  property: Property;
  desiredMoveInDate?: string;
  disableDecisionFlow?: boolean;
  applicationStatus: ApplicationStatus;
  submittedAt: string | null;
  setShowNotes: (value: boolean) => void;
  setShowDecisionFlowModal?: (isOpen: boolean) => void;
  setDecisionConfirmationModal?: (isOpen: boolean) => void;
  setShowInviteApplicantsModal?: (isOpen: boolean) => void;
  viewTemplate: () => void;
  onCancelClick?: () => void;
  includeCoSignerInCriteriaCalculation?: boolean;
  isRuleBased?: boolean;
  onMoveClick?: () => void;
  onRefundClick?: () => void;
}

export const LayoutHeader = ({
  applicationId,
  applicants,
  desiredMoveInDate,
  property,
  unit,
  disableDecisionFlow,
  applicationStatus,
  viewTemplate,
  setShowNotes,
  setShowDecisionFlowModal,
  setDecisionConfirmationModal,
  setShowInviteApplicantsModal,
  onCancelClick,
  includeCoSignerInCriteriaCalculation,
  isRuleBased,
  onMoveClick,
  onRefundClick
}: ILayoutHeader) => {
  const { permissions, type } = useAuthContext();
  const actions = usePopoverActions({
    applicants,
    onMoveClick,
    setShowNotes,
    viewTemplate,
    onRefundClick,
    onCancelClick,
    applicationStatus,
    setShowDecisionFlowModal,
    setDecisionConfirmationModal,
    setShowInviteApplicantsModal
  });
  const [includeCoSigner, setIncludeCoSigner] = useState<boolean>(
    includeCoSignerInCriteriaCalculation || false
  );

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationKey: ["APPLICATION", applicationId],
    mutationFn: () => {
      return instance.patch(
        `portal/v1/applications/${applicationId}/include_co_signer`,
        {
          include_co_signer: !includeCoSigner
        }
      );
    },
    onSuccess: () => {
      setIncludeCoSigner(!includeCoSigner);
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err.message));
    }
  });

  return (
    <div>
      <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 py-3 md:flex-nowrap flex-wrap">
        <div className="flex items-center gap-2.5 md:h-auto h-5">
          <BreadCrumbs
            crumbs={[
              {
                title: "Applications",
                href: "/applications"
              },
              {
                title:
                  applicants
                    ?.map((app) =>
                      app.first_name && app.last_name
                        ? getFullName({
                            firstName: app.first_name,
                            lastName: app.last_name,
                            middleName: app.middle_name,
                            short: true
                          })
                        : app.email
                          ? app.email
                          : formatPhone(app.phone)
                    )
                    .join(", ") || "application with invitees",
                href: `/applications/${applicationId}`
              }
            ]}
          />
        </div>
        <div className="flex">
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            Icon={Settings04}
            className="hidden md:flex"
            onClick={() => {
              viewTemplate();
            }}
          >
            Screening Settings
          </Button>
          <Button
            borderLess
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            className="hidden md:flex"
            Icon={StickerSquare}
            onClick={() => setShowNotes(true)}
          >
            Notes
          </Button>
        </div>
      </div>
      <ApplicationStatusBanner status={applicationStatus} />
      <div className="flex items-center justify-between border-b border-alpha-40 p-3 md:p-6 flex-wrap md:flex-nowrap">
        <div>
          <p className="text-sm text-content-400">Application Overview</p>
          <h4 className="text-xl font-bold text-content-1000">
            {applicants
              ?.map((app) =>
                getFullName({
                  firstName: app.first_name,
                  lastName: app.last_name,
                  middleName: app.middle_name,
                  short: true
                })
              )
              .join(", ")}
          </h4>
        </div>
        <div className="flex items-center justify-end gap-2">
          {type.isAdmin || type.isOwner || permissions.canCancelApplications ? (
            <ContextMenuPopover borderLess={false} actions={actions} />
          ) : null}
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => setShowInviteApplicantsModal?.(true)}
            disabled={
              ![ApplicationStatus.Pending, ApplicationStatus.Started].includes(
                applicationStatus
              )
            }
            className="hidden md:flex"
            tooltip={
              [
                ApplicationStatus.Approved,
                ApplicationStatus.Rejected,
                ApplicationStatus.ConditionalApproved,
                ApplicationStatus.Canceled,
                ApplicationStatus.Merged
              ].includes(applicationStatus)
                ? "You can't invite applicants once the decisions have been made"
                : undefined
            }
          >
            Invite Applicant
          </Button>
          {applicationStatus === ApplicationStatus.Approved ||
          applicationStatus === ApplicationStatus.Rejected ||
          applicationStatus === ApplicationStatus.ConditionalApproved ||
          (!permissions.canMakeDecisions && type.isPropertyManager) ? null : (
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={() => {
                applicationStatus === ApplicationStatus.Submitted
                  ? setShowDecisionFlowModal?.(true)
                  : setDecisionConfirmationModal?.(true);
              }}
              className="hidden md:flex"
              disabled={disableDecisionFlow}
              tooltip={
                applicationStatus !== ApplicationStatus.Submitted
                  ? `You can make a decision when all members of this group complete their application`
                  : undefined
              }
            >
              Make Decision
            </Button>
          )}
        </div>
      </div>
      <div className="flex justify-between p-3 md:p-6 flex-wrap md:flex-nowrap">
        <div className="flex items-center justify-start gap-2 md:gap-10 flex-wrap md:flex-nowrap">
          <div className="flex items-center gap-10">
            <div className="-mr-6 flex h-10 w-10 items-center shrink-0 justify-center rounded-lg bg-primary-20">
              <Home02 className="stroke-primary-500" size="20" />
            </div>
            <div>
              <p className="text-sm font-normal text-content-400">Property</p>
              <p className="text-sm font-medium capitalize text-content-1000">
                {`${property.address.address1}, ${property.address.city}, ${property.address.state_or_province} ${property.address.postal_code}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-10">
            <div>
              <p className="text-sm font-normal text-content-400">Unit</p>
              <p className="text-sm font-medium capitalize text-content-1000">
                {unit.title || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm font-normal text-content-400">
                Listing rent
              </p>
              <p className="text-sm font-medium text-content-1000">
                {unit.monthly_rent_amount
                  ? `${formatToCurrency(unit.monthly_rent_amount, { maximumFractionDigits: 0 })}`
                  : "--"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-10">
            <div>
              <p className="text-sm font-normal text-content-400">Available</p>
              <p className="text-sm font-medium text-content-1000">
                {unit.available_at && moment(unit.available_at).isValid()
                  ? moment(unit.available_at).utc().format("MMM D, YYYY")
                  : "--"}
              </p>
            </div>
            <div>
              <p className="text-sm font-normal text-content-400">
                Desired move-in
              </p>
              <p className="text-sm font-medium text-content-1000">
                {desiredMoveInDate && moment(desiredMoveInDate).isValid()
                  ? moment(desiredMoveInDate).utc().format("MMM D, YYYY")
                  : "--"}
              </p>
            </div>
          </div>
        </div>

        {isPending ? (
          <Loader size={25} />
        ) : (
          <div className="flex items-center justify-end whitespace-nowrap">
            <p className="text-xs font-normal text-content-300 mr-2">
              {isRuleBased
                ? "Include guarantors in group metrics"
                : "Include guarantors in application score"}
            </p>
            <Checkbox
              disabled={
                applicants?.filter(
                  (applicant) => applicant.role === ApplicantType.CoSigner
                ).length === 0
              }
              checked={Boolean(includeCoSigner)}
              onChange={() => mutate()}
            />
          </div>
        )}
      </div>
    </div>
  );
};
